import React from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Target, Users, Award, Shield, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const About = () => {
  const values = [
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Specialization',
      description: 'Focused exclusively on supporting print production environments and graphic arts businesses.'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Professionalism',
      description: 'Committed to delivering reliable, technically proficient support for your operations.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Long-Term Relationships',
      description: 'Building collaborative partnerships based on trust and operational alignment.'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Industry Knowledge',
      description: 'Deep understanding of print workflows, technical requirements, and customer service needs.'
    }
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="page-hero py-24 bg-gradient-to-br from-cyan-50 to-green-50">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl lg:text-6xl font-bold mb-6">
            Helping Print Companies <span className="text-gradient">Operate More Efficiently</span>
          </h1>
          <p className="text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto">
            Specialized remote support for the printing and graphic production industry
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold mb-8 text-center">Our Mission</h2>
            <div className="space-y-6 text-xl text-gray-700 leading-relaxed">
              <p>
                Our mission is to provide specialized remote support that helps print businesses improve customer service, optimize workflows, and reduce operational bottlenecks.
              </p>
              <p>
                We focus on professionalism, industry knowledge, and long-term business relationships.
              </p>
              <p>
                Latin Talent Remote was created to address a specific need in the printing industry: reliable, technically knowledgeable remote professionals who understand print production environments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide how we work with print businesses
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="p-8 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="icon-wrapper w-16 h-16 rounded-full bg-gradient-to-r from-cyan-500 to-green-500 flex items-center justify-center mb-6 text-white mx-auto">
                  {value.icon}
                </div>
                <h3 className="text-2xl font-bold mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold mb-12 text-center">Our Approach</h2>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                We understand that print production businesses need more than generic administrative support. They need professionals who understand graphic arts terminology, file preparation requirements, and production workflows.
              </p>
              <p>
                That's why we focus exclusively on the printing industry. Our recruitment process identifies professionals with relevant technical knowledge and customer service skills.
              </p>
              <p>
                We provide training on print industry standards, software familiarity, and workflow processes to ensure our team members can integrate effectively into your operations.
              </p>
              <p>
                Our goal is to deliver remote support that strengthens your business operations and improves efficiency.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Deliver */}
      <section className="py-24 bg-gradient-to-br from-cyan-50 to-green-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold mb-12 text-center">What We Deliver</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="p-8 hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-2xl font-bold mb-4">For Your Business</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-600 font-bold">•</span>
                    <span>Improved customer service operations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-600 font-bold">•</span>
                    <span>Streamlined file management workflows</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-600 font-bold">•</span>
                    <span>Reduced operational bottlenecks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-600 font-bold">•</span>
                    <span>Cost-efficient staffing solutions</span>
                  </li>
                </ul>
              </Card>
              <Card className="p-8 hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-2xl font-bold mb-4">Our Professionals</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-600 font-bold">•</span>
                    <span>Industry-specific training</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-600 font-bold">•</span>
                    <span>Technical software familiarity</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-600 font-bold">•</span>
                    <span>Customer service skills</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-600 font-bold">•</span>
                    <span>Workflow understanding</span>
                  </li>
                </ul>
              </Card>
              <Card className="p-8 hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-2xl font-bold mb-4">Our Commitment</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-600 font-bold">•</span>
                    <span>Reliable support</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-600 font-bold">•</span>
                    <span>Professional communication</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-600 font-bold">•</span>
                    <span>Continuous improvement</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-600 font-bold">•</span>
                    <span>Long-term partnership</span>
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why Not Generic VAs */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-cyan-50 to-green-50 p-12 rounded-2xl">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6 text-center">We Are Not a Generic Virtual Assistant Agency</h2>
            <p className="text-xl text-gray-700 leading-relaxed text-center mb-8">
              We specialize exclusively in supporting print production environments.
            </p>
            <div className="grid md:grid-cols-2 gap-6 text-gray-700">
              <div>
                <h3 className="font-bold text-xl mb-3">Generic VAs:</h3>
                <ul className="space-y-2">
                  <li>• General administrative support</li>
                  <li>• Limited industry knowledge</li>
                  <li>• Basic file handling</li>
                  <li>• Generic customer service</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-xl mb-3">Latin Talent Remote:</h3>
                <ul className="space-y-2">
                  <li>• Print industry specialization</li>
                  <li>• Technical software familiarity</li>
                  <li>• Print file knowledge</li>
                  <li>• Industry-specific customer service</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-24 bg-gradient-to-r from-cyan-500 to-green-500 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">Let's Work Together</h2>
          <p className="text-xl lg:text-2xl mb-10 opacity-90 max-w-3xl mx-auto">
            Discover how specialized remote support can improve your print business operations
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-cyan-600 hover:bg-gray-100 text-lg px-10 py-6 font-semibold">
              Schedule a Consultation
              <ArrowRight className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
